import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/feature/PageHeader';
import FigureCard from '@/components/feature/FigureCard';
import { figures } from '@/mocks/figures';
import { siteName } from '@/mocks/site';

const fields = ['全部', ...Array.from(new Set(figures.map((f) => f.field)))];

const figuresJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'CollectionPage',
      name: '档案人物库｜独家黑料档案中心',
      inLanguage: 'zh-CN',
      description: '按人物归档的独家黑料人物库，收录公开履历、作品署名与关联事件的时间线。',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hnitsc.cn/' },
        { '@type': 'ListItem', position: 2, name: '档案人物库', item: 'https://hnitsc.cn/figures' },
      ],
    },
    {
      '@type': 'ItemList',
      name: '档案人物列表',
      itemListElement: figures.map((f, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: f.name,
        description: f.summary,
      })),
    },
  ],
};

export default function FiguresPage() {
  const [keyword, setKeyword] = useState('');
  const [field, setField] = useState('全部');
  const [sort, setSort] = useState<'heat' | 'records'>('heat');

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    const list = figures.filter((f) => {
      const matchField = field === '全部' || f.field === field;
      const matchKw =
        !kw ||
        f.name.includes(kw) ||
        f.summary.includes(kw) ||
        f.tags.some((t) => t.includes(kw));
      return matchField && matchKw;
    });
    return [...list].sort((a, b) => (sort === 'heat' ? b.heat - a.heat : b.recordCount - a.recordCount));
  }, [keyword, field, sort]);

  return (
    <>
      <PageHeader
        crumbs={[
          { label: '首页', to: '/' },
          { label: '档案人物库' },
        ]}
        eyebrow="档案人物库"
        title="按人物归档，从公开履历一路读到事件现场"
        description="人物库以公开作品、公开活动与可核对的记录为基础。每位人物的条目都按时间顺序排列，并标注材料来源类型，方便你顺着一条脉络往下读。"
        image="https://readdy.ai/api/search-image?query=Long%20shelves%20of%20neatly%20labeled%20archive%20boxes%20in%20a%20quiet%20reading%20room%2C%20warm%20amber%20light%2C%20aged%20cream%20tones%2C%20editorial%20archival%20atmosphere%2C%20no%20people&width=1400&height=600&seq=figures-banner-04&orientation=landscape"
        imageAlt="档案室书架与档案盒，象征独家黑料档案人物库"
        imageTitle="档案人物库 · 独家黑料档案中心"
      />

      <section className="w-full bg-background-50">
        <div className="mx-auto max-w-content px-4 py-10 md:px-8 md:py-14">
          <div className="flex flex-col gap-5 rounded-lg border border-background-200 bg-background-100 p-4 md:flex-row md:items-center md:justify-between md:p-5">
            <div className="flex w-full items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3 md:max-w-md">
              <i className="ri-search-line text-base text-foreground-400" aria-hidden="true"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索人物姓名、标签或档案摘要"
                aria-label="搜索人物档案"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
                {fields.map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setField(f)}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                      field === f
                        ? 'bg-primary-700 text-background-50'
                        : 'text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
                <button
                  type="button"
                  onClick={() => setSort('heat')}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    sort === 'heat' ? 'bg-secondary-700 text-background-50' : 'text-foreground-700'
                  }`}
                >
                  按热度
                </button>
                <button
                  type="button"
                  onClick={() => setSort('records')}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    sort === 'records' ? 'bg-secondary-700 text-background-50' : 'text-foreground-700'
                  }`}
                >
                  按记录数
                </button>
              </div>
            </div>
          </div>

          <p className="mt-4 text-sm text-foreground-500">
            共找到 <strong className="text-foreground-900">{filtered.length}</strong> 位人物档案
            {field !== '全部' && ` · 领域：${field}`}
          </p>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {filtered.map((figure) => (
                <FigureCard key={figure.id} figure={figure} />
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-md bg-background-200 text-foreground-500">
                <i className="ri-inbox-archive-line text-2xl" aria-hidden="true"></i>
              </span>
              <p className="mt-4 font-heading text-base font-semibold text-foreground-950">
                暂未匹配到人物档案
              </p>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                试试更换关键词，或切换到“全部”领域重新查看。也可以直接浏览秘史专题。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword('');
                  setField('全部');
                }}
                className="mt-5 whitespace-nowrap rounded-md bg-primary-700 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
              >
                重置筛选条件
              </button>
            </div>
          )}

          <div className="mt-12 flex flex-col items-start justify-between gap-4 rounded-lg border border-primary-200 bg-primary-100 p-6 md:flex-row md:items-center">
            <div>
              <h2 className="font-heading text-lg font-bold text-primary-900">想看更长的脉络？</h2>
              <p className="mt-1 text-sm text-primary-800">
                人物条目适合看单点，秘史专题会把多个人物放进同一条时间线里对照。
              </p>
            </div>
            <Link
              to="/topics"
              className="whitespace-nowrap rounded-md bg-primary-700 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
            >
              前往秘史专题
            </Link>
          </div>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(figuresJsonLd) }} />
      <span className="sr-only">{siteName}档案人物库</span>
    </>
  );
}